import { faFacebook, faInstagram, faTiktok, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import { useState } from 'react';

import { SocialMediaLinks } from '@/components/atoms/SocialMediaLinks';
import { PlayerBiographyModal } from '@/components/molecules/modals/PlayerBiographyModal';
import { Player } from '@/entities/player-details/PlayerBanner';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { convertHeightToCm, truncateToThreeDecimals } from '@/utils/helpers';

interface Props {
	player: Player;
	className?: string;
}

export const PlayerDetailsProfileStats = ({ player, className }: Props) => {
	const { showPlayerRatingsSingles, showPlayerRatingsDoubles, showPlayerRatingsSkinnySingles } = useTeamLeagueInfo();

	const [isPlayerBioModalOpen, setIsPlayerBioModalOpen] = useState(false);

	const socialMediaLinks = [
		{ url: player.facebook, icon: faFacebook },
		{ url: player.twitter, icon: faXTwitter },
		{ url: player.instagram, icon: faInstagram },
		{ url: player.youtube, icon: faYoutube },
		{ url: player.tiktok, icon: faTiktok }
	];

	const showPlayerRatings = showPlayerRatingsDoubles || showPlayerRatingsSingles || showPlayerRatingsSkinnySingles;

	return (
		<div className={`flex-col w-full md:w-fit mt-6 md:mt-0 ${className}`}>
			<div className="flex flex-col items-center md:items-start justify-center gap-2 w-full md:w-fit">
				{player.seasonRank ? (
					<div className="flex items-center justify-center gap-4 md:gap-3 xl:gap-4 px-6 md:px-3 lg:px-3 xl:px-6 py-1 bg-gray-100 rounded-lg min-h-[52px] w-fit">
						<span className="text-center text-base lg:text-lg font-semibold leading-7 text-gray-600">Season Ranking </span>
						<div className="h-full w-px bg-gray-400" />
						<span className="text-center text-2xl lg:text-4xl font-bold leading-[44px] tracking-[-0.72px] text-gray-600">
							{player.seasonRank}
						</span>
					</div>
				) : null}

				<div className="flex items-center justify-center gap-4 md:gap-3 xl:gap-4 px-6 md:px-3 lg:px-3 xl:px-6 py-1 bg-gray-100 rounded-lg min-h-[52px] w-fit">
					{!!showPlayerRatings && (
						<div className="flex flex-col justify-center items-center">
							<span className="text-center text-sm font-normal leading-5 text-gray-600">DUPR Rating</span>

							<div className="flex flex-row items-center justify-center gap-2 text-sm lg:text-base font-normal leading-6 text-gray-600">
								{showPlayerRatingsDoubles ? (
									<span className="font-bold">D: {truncateToThreeDecimals(player.ratingDoubles)}</span>
								) : null}
								{showPlayerRatingsSingles ? <span>S: {truncateToThreeDecimals(player.ratingSingles)}</span> : null}
								{showPlayerRatingsSkinnySingles ? <span>SS: {truncateToThreeDecimals(player.skinnySinglesRating)}</span> : null}
							</div>
						</div>
					)}

					<div className="h-full w-px bg-gray-400" />

					<div className="flex flex-col justify-center items-center">
						<span className="text-center text-sm font-normal leading-5 text-gray-600">DUPR ID</span>
						<span className="text-sm lg:text-base font-bold leading-6 text-gray-600">{player.duprId || '-'}</span>
					</div>
				</div>

				<div className="flex items-center justify-center gap-4 md:gap-3 xl:gap-4 px-6 md:px-3 lg:px-3 xl:px-6 py-1 bg-gray-100 rounded-lg min-h-[52px] w-fit">
					<div className="flex flex-col justify-center items-center">
						<span className="text-center text-sm font-normal leading-5 text-gray-600">Age</span>

						{!player.showAgeToPublic || !player.age ? (
							<TooltipProvider delayDuration={0} skipDelayDuration={0}>
								<Tooltip>
									<TooltipTrigger asChild>
										<FontAwesomeIcon icon={faEyeSlash} className="ml-0.5" />
									</TooltipTrigger>
									<TooltipContent className="bg-gray-900">
										{!player.showAgeToPublic ? (
											<p className="z-20">Player age hidden by player</p>
										) : (
											<p className="z-20">No age information available</p>
										)}
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						) : (
							<span className="text-sm lg:text-base font-bold leading-6 text-gray-600">{player.age}</span>
						)}
					</div>

					<div className="h-full w-px bg-gray-400" />

					<div className="flex flex-col justify-center items-center">
						<span className="text-center text-sm font-normal leading-5 text-gray-600">Gender</span>
						<span className="text-sm lg:text-base font-bold leading-6 text-gray-600">{player.gender}</span>
					</div>

					<div className="h-full w-px bg-gray-400" />

					<div className="flex flex-col justify-center items-center text-center">
						<span className="text-center text-sm font-normal leading-5 text-gray-600">Height</span>
						<span className="text-sm lg:text-base font-bold leading-6 text-gray-600">
							{player.heightFeet || player.heightInches
								? `${player.heightFeet}'${player.heightInches}” (${convertHeightToCm(player.heightFeet, player.heightInches)}cm)`
								: '-'}
						</span>
					</div>

					<div className="h-full w-px bg-gray-400" />

					<div className="flex flex-col justify-center items-center text-center">
						<span className="text-center text-sm font-normal leading-5 text-gray-600">Plays</span>
						<span className="text-sm lg:text-base font-bold leading-6 text-gray-600">{player.handedness}</span>
					</div>
				</div>
			</div>

			{!!player.playerBio && (
				<div className="flex items-center justify-center md:justify-start mt-3">
					<Button variant="outline" size="sm" className="w-fit" onClick={() => setIsPlayerBioModalOpen(true)}>
						Read bio
					</Button>
				</div>
			)}

			<SocialMediaLinks
				socialMediaLinks={socialMediaLinks}
				className="flex items-center justify-center md:justify-start gap-2.5 p-2.5 text-white w-full md:w-fit mt-1"
				linkClassName="flex justify-center items-center size-9 bg-black rounded-full"
				iconClassName="!size-[22px]"
			/>

			{!!isPlayerBioModalOpen && <PlayerBiographyModal playerBio={player.playerBio} onClose={() => setIsPlayerBioModalOpen(false)} />}
		</div>
	);
};
